import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import { formatPrice } from "@ckgo2/shared/utils";
import { formatDate } from "@/lib/format";
import FacetedListLayout from "@/components/common/FacetedListLayout";
import FacetPanel, { type FacetBucket } from "@/components/common/FacetPanel";
import { User, Mail, Phone, MapPin, Calendar, ShoppingCart, Plus, CreditCard } from "lucide-react";

interface CustomerRow {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  email: string;
  phone?: string | null;
  city?: string | null;
  country?: string | null;
  source?: string | null;
  totalSpent?: number | null;
  tripsCount?: number | null;
  newsletterSubscribed?: boolean;
  createdAt: string;
}

interface FacetedCustomers {
  data: CustomerRow[];
  total: number;
  page: number;
  limit: number;
  facets: Record<string, FacetBucket[]>;
  summary: { newsletterCount: number; newThisMonth: number };
  success: true;
}

export default function CustomersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const qs = searchParams.toString();

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["customers", qs],
    queryFn: () => api.get<FacetedCustomers>(`/api/admin/customers?${qs}`),
  });

  const customers = data?.data ?? [];
  const facets = data?.facets ?? {};
  const total = data?.total ?? 0;
  const limit = data?.limit ?? 25;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t("crm.customers.title")}
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">{t("crm.customers.subtitle")}</p>
        </div>
        <Link to="/customers/new" className="btn-primary">
          <Plus className="mr-2 h-5 w-5" />
          {t("crm.customers.newCustomer")}
        </Link>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <div className="card">
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
            {t("crm.customers.totalCustomers")}
          </p>
          <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">{total}</p>
        </div>
        <div className="card">
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
            {t("crm.customers.newsletterSubscribers")}
          </p>
          <p className="text-warning-600 mt-1 text-2xl font-bold">
            {data?.summary?.newsletterCount ?? 0}
          </p>
        </div>
        <div className="card">
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
            {t("crm.customers.newThisMonth")}
          </p>
          <p className="text-success-600 mt-1 text-2xl font-bold">
            {data?.summary?.newThisMonth ?? 0}
          </p>
        </div>
      </div>

      <FacetedListLayout
        searchPlaceholder={t("crm.customers.searchPlaceholder")}
        total={total}
        limit={limit}
        isLoading={isLoading}
        isFetching={isFetching}
        filters={
          <>
            <FacetPanel
              title={t("crm.fields.country")}
              paramKey="country"
              order="name"
              buckets={facets.country ?? []}
            />
            <FacetPanel
              title={t("crm.customers.source")}
              paramKey="source"
              buckets={facets.source ?? []}
              nullLabel="—"
            />
          </>
        }
      >
        <div className="card overflow-hidden p-0">
          {customers.length === 0 ? (
            <div className="flex h-64 flex-col items-center justify-center text-gray-500">
              <User className="mb-2 h-12 w-12 text-gray-300" />
              <p>{t("crm.customers.noCustomers")}</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50">
                <tr>
                  {[
                    t("crm.customers.table.customer"),
                    t("crm.customers.table.contact"),
                    t("crm.customers.table.address"),
                    t("crm.customers.table.registered"),
                    t("crm.customers.table.trips"),
                    t("crm.customers.table.spent"),
                  ].map((h) => (
                    <th
                      key={h}
                      className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {customers.map((customer) => (
                  <tr
                    key={customer.id}
                    onClick={() => navigate(`/customers/${customer.id}`)}
                    className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400 flex h-10 w-10 items-center justify-center rounded-full font-medium">
                          {customer.firstName?.[0]}
                          {customer.lastName?.[0]}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {customer.firstName} {customer.lastName}
                          </p>
                          {customer.newsletterSubscribed && (
                            <span className="badge bg-primary-100 text-primary-700 text-xs">
                              {t("crm.customers.newsletterBadge")}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1 text-sm">
                        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                          <Mail className="h-4 w-4" />
                          {customer.email}
                        </div>
                        {customer.phone && (
                          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                            <Phone className="h-4 w-4" />
                            {customer.phone}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {customer.city ? (
                        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                          <MapPin className="h-4 w-4" />
                          {customer.city}
                          {customer.country && `, ${customer.country}`}
                        </div>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                        <Calendar className="h-4 w-4" />
                        {formatDate(customer.createdAt)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                        <ShoppingCart className="h-4 w-4" />
                        {customer.tripsCount || 0}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-white">
                        <CreditCard className="h-4 w-4 text-gray-400" />
                        {formatPrice(customer.totalSpent || 0)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </FacetedListLayout>
    </div>
  );
}
